import React, { memo, useRef, useEffect, useMemo, Fragment } from 'react';
import moment from 'moment';
import Checkbox from '@material-ui/core/Checkbox';
import { generatePDF } from '@abhisek507/html2multipagepdf';
import {
  Letter,
  Quality as ImageQuality,
  RegularPageSelector as pageSelector,
  PageElementSelector as pageElementSelector,
  PageWithMaxPossibleWidthSelector as pageWithMaxPossibleWidthSelector,
} from '@abhisek507/html2multipagepdf/constants';
import Input from '../../subComponents/input';
import Button from '../../subComponents/button';
import DateWrapper from '../../subComponents/date-wrapper';
import { SectionLabel, SignatureWrapper, QuestionAnswers, SOAPNotes } from '../Eversense';
import {
  PID_ShouldCabotegravir,
  QID_CurrentlyTakingAppretudeCheckbox,
  QID_DrugAllergiesInput,
  QID_IsPatientPregnantCheckbox,
  QID_LastInjectionDate,
  DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING,
} from '../../../constants/cmnTemplateForApretude.constant';
import { Apretude } from '../../../constants/patientChart.constant';
import {
  DateFormat,
  CheckBoxChecked,
  DEFAULT_FORMFIELD,
  MaxDate,
  DEFAULT_PDF_INFO,
  FAX_ProvidersAndCreatePDF,
  Email_ProvidersAndCreatePDF,
} from '../../../constants/cmnTemplateForEversense.constant';
import ViiVLogo from '../../../assets/viivConnectLogo.svg';
import { preparePDF } from '../../../utils/referralForm';
import './styles.css';

export const CMNApretudeTemplateIdentifier = 'cmn-template-for-apretude';

const Heading = memo(({
  value: {
    sentTo,
    sentDate,
  },
}) => (
  <div className='heading flex flex-column gap-8 align-items-center justify-content-space-between m-b-5'>
    <div className='flex min-width-180 width-180 heading-logo'>
      <div className='flex align-items-center justify-content-center'>
        <img src={ViiVLogo} alt='viiv_connect_logo' className='height-40 width-180' />
      </div>
    </div>

    <div className='flex flex-column gap-8 min-width-40 align-items-center'>
      <span className='font-12 tracking-normal'>ViiVConnect provides comprehensive information on access and coverage to help Patients get their prescribed ViiV Healthcare medications.</span>
    </div>

    <div className='flex gap-8 align-items-center justify-content-center font-24'>
      <span className='bold'>APRETUDE</span>
      <span className=''>(cabotegravir)</span>
      <span className='bold'>Alternative Site of Care (ASOC) Referral/Order Form</span>
    </div>

    <div className='flex flex-column gap-8 min-width-40 align-items-center'>
      <p className='m-0 p-0 font-16 bold tracking-wide'>Doctor Matrix Medical Group - Telehealth Program</p>
    </div>
  </div>
));

const Footer = memo(({
  onSubmit: handleSubmit = () => {}
}) => (
  <div className='footer flex flex-column gap-16'>
    <p className='flex font-10 line-14 m-0'>
    CONFIDENTIAL HEALTH INFORMATION: Healthcare information is personal information related to a person's healthcare. It is being faxed to you after appropriate authorization or under circumstances that
do not require authorization. You are obligated to maintain it in a safe, secure, and confidential manner. Re-disclosure of this information is prohibited unless permitted by law or appropriate customer/patient
authorization is obtained. Unauthorized re-disclosure or failure to maintain confidentiality could subject you to penalties described in federal and state laws. IMPORTANT WARNING: This message is intended
for the use of the person or entity to whom it is addressed and may contain information that is privileged and confidential, the disclosure of which is governed by applicable law. If the reader of this message is
not the intended recipient, or the employee or agent responsible for delivering it to the intended recipient, you are hereby notified that any dissemination, distribution or copying of this information is STRICTLY
PROHIBITED. If you have received this message in error, please notify us immediately. Brand names are the property of their respective owners.
    </p>
    <div className='flex justify-content-flex-end d-none'>
      <Button
        variant='contained'
        color='primary'
        onClick={handleSubmit}
        label='Download Template'
      />
    </div>
  </div>
));

const Date = memo(({
  value: {
    signatureDate,
  },
}) => (
  <div className='section patient-information border-none'>
    <div className='flex flex-row-reverse gap-8'>
      <DateWrapper name='signatureDate' label='Date:' value={signatureDate} max={MaxDate} />
    </div>
  </div>
));

export const PatientInformation = memo(({
  value: {
    patientDob,
    patientFullName,
    patientAddress,
    patientState,
    patientZip,
    patientCity,
    patientPhone,
    patientEmail,
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section patient-information m-t-8 m-b-5'>
    <SectionLabel display='PATIENT INFORMATION' />

    <div className='flex flex-column gap-8 p-4'>
      <div className='flex align-items-center'>
        <Input name='patientFullName' label='Patient Last, First Name:' className='flex flex-fill m-r-4' value={patientFullName} onChange={handleChange.bind(this, 'patientFullName')} />
        <DateWrapper name='patientDob' label='Date of Birth:' value={patientDob} onChange={handleChange.bind(this, 'patientDob')} />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientAddress' label='Patient Address:' className='flex flex-fill m-r-4' value={patientAddress} onChange={handleChange.bind(this, 'patientAddress')} />
        <Input
          name='patientState'
          label='State:'
          className='m-r-4'
          inputClassName='max-width-165'
          value={patientState}
          onChange={handleChange.bind(this, 'patientState')}
        />
        <Input
          name='patientZip'
          label='Zip:'
          inputClassName='max-width-90'
          value={patientZip}
          onChange={handleChange.bind(this, 'patientZip')}
        />
      </div>

      <div className='flex align-items-center'>
        <Input name='patientCity' label='City:' className='flex flex-fill m-r-4' value={patientCity} onChange={handleChange.bind(this, 'patientCity')} />
        <Input name='patientEmail' label='Email:' className='min-width-260 m-r-4' value={patientEmail} onChange={handleChange.bind(this, 'patientEmail')} />
        <Input name='patientPhone' label='Phone:' value={patientPhone} onChange={handleChange.bind(this, 'patientPhone')} />
      </div>
    </div>
  </div>
));

const ClinicalInformation = memo(({
  value: {
    patientHeight,
    patientWeight,
    diagnosisDescription,
    drugAllergies,
    ...rest
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section clinical-information m-t-8 m-b-5 p-4'>
    <SectionLabel display='DIAGNOSIS & CLINICAL INFORMATION' />

    <div className='flex gap-16 align-items-center m-b-5'>
      <div className='flex flex-fill align-items-center'>
        <Input name='diagnosisDescription' label='Primary Diagnosis Description:' className='flex flex-fill' value={diagnosisDescription} onChange={handleChange.bind(this, 'diagnosisDescription')} />
      </div>
      <div className='flex align-items-center'>
        <Input
          name='patientHeight'
          label='Height (in.):'
          className='flex'
          inputClassName='width-60'
          value={patientHeight}
          onChange={handleChange.bind(this, 'patientHeight')}
        />
      </div>
      <div className='flex align-items-center'>
        <Input
          name='patientHeight'
          label='Weight (lbs):'
          className='flex'
          inputClassName='width-60'
          value={patientWeight}
          onChange={handleChange.bind(this, 'patientWeight')}
        />
      </div>
    </div>

    <div className='flex align-items-center m-b-5'>
      <Input name='drugAllergies' label='Drug Allergies:' className='flex flex-fill' value={drugAllergies} onChange={handleChange.bind(this, 'drugAllergies')} />
    </div>

    <div className='flex gap-8 align-items-center'>
      <div className='flex align-items-center'>
        <label className='input-label'>Is the patient pregnant, planning a pregnancy, or nursing?*</label>
        <span className='required font-24'>*</span>
      </div>

      <div className='flex align-items-center width-60'>
        <Checkbox
          title="checkbox-pregnant-yes"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-pregnant'] === CheckBoxChecked.yes}
          onChange={(_, _value) => { handleChange('checkbox-pregnant', { target: { value: _value ? CheckBoxChecked.yes : '' } }); }}
        />
        <label>Yes</label>
      </div>

      <div className='flex align-items-center width-60'>
        <Checkbox
          title="checkbox-pregnant-no"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-pregnant'] === CheckBoxChecked.no}
          onChange={(_, _value) => { handleChange('checkbox-pregnant', { target: { value: _value ? CheckBoxChecked.no : '' } }); }}
        />
        <label>No</label>
      </div>
    </div>
  </div>
));

const PatientStatus = memo(({
  value: {
    lastInjectionDate,
    nextInjectionDate,
    cabotegravirStartDate,
    ...rest
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section patient-status m-t-8 m-b-5 p-4'>
    <SectionLabel display='PATIENT STATUS' />

    <div className='flex gap-16 align-items-center m-b-2'>
      <div className='flex align-items-center'>
        <Checkbox
          title="checkbox-ntoth"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-ntoth']}
          onChange={(_, _value) => { handleChange('checkbox-ntoth', { target: { value: _value } }); }}
        />
        <label className='input-label'>New to Therapy</label>
      </div>

      <div className='flex align-items-center'>
        <Checkbox
          title="checkbox-conth"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-conth']}
          onChange={(_, _value) => { handleChange('checkbox-conth', { target: { value: _value } }); }}
        />
        <label className='input-label'>Continuing Therapy</label>
      </div>

      <div className='flex align-items-center'>
        <Checkbox
          title="checkbox-thch"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-thch']}
          onChange={(_, _value) => { handleChange('checkbox-thch', { target: { value: _value } }); }}
        />
        <label className='input-label'>Therapy Change</label>
      </div>
    </div>

    <div className='flex gap-16 align-items-center m-b-2'>
      <div className='flex gap-8 align-items-center'>
        <label className='input-label'>If New to Therapy,</label>
        <label>will the patient be started on oral lead-in of VOCABRIA (cabotegravir)?</label>
      </div>

      <div className='flex align-items-center width-60'>
        <Checkbox
          title="checkbox-should-cabotegravir-yes"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-should-cabotegravir'] === CheckBoxChecked.yes}
          onChange={(_, _value) => { handleChange('checkbox-should-cabotegravir', { target: { value: _value ? CheckBoxChecked.yes : '' } }); }}
        />
        <label>Yes</label>
      </div>

      <div className='flex align-items-center width-60'>
        <Checkbox
          title="checkbox-should-cabotegravir-no"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-should-cabotegravir'] === CheckBoxChecked.no}
          onChange={(_, _value) => { handleChange('checkbox-should-cabotegravir', { target: { value: _value ? CheckBoxChecked.no : '' } }); }}
        />
        <label>No</label>
      </div>
    </div>

    <div className='flex gap-16 align-items-center m-b-2'>
      <div className='width-30'></div>

      <div className='flex gap-8 align-items-center'>
        <label className='input-label'>If yes,</label>
        <label>has the patient started oral lead-in of VOCABRIA (cabotegravir)?</label>
      </div>

      <div className='flex align-items-center width-60'>
        <Checkbox
          title="checkbox-has-cabotegravir-yes"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-has-cabotegravir'] === CheckBoxChecked.yes}
          onChange={(_, _value) => { handleChange('checkbox-has-cabotegravir', { target: { value: _value ? CheckBoxChecked.yes : '' } }); }}
        />
        <label>Yes</label>
      </div>

      <div className='flex align-items-center width-60'>
        <Checkbox
          title="checkbox-has-cabotegravir-no"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-has-cabotegravir'] === CheckBoxChecked.no}
          onChange={(_, _value) => { handleChange('checkbox-has-cabotegravir', { target: { value: _value ? CheckBoxChecked.no : '' } }); }}
        />
        <label>No</label>
      </div>
    </div>

    <div className='flex gap-16 align-items-center m-b-2'>
      <div className='width-30'></div>

      <div className='flex gap-8 align-items-center'>
        <label className='input-label'>If yes,</label>
        <label>please enter start date:</label>
      </div>

      <div className='flex align-items-center'>
        <DateWrapper name='cabotegravirStartDate' label='' value={cabotegravirStartDate} onChange={handleChange.bind(this, 'cabotegravirStartDate')} />
      </div>
    </div>

    <div className='flex gap-16 height-30'>
      <div className='flex gap-12 height-30 align-items-center'>
        <div className='flex gap-8 align-items-center'>
          <label className='input-label'>If Continuing Therapy,</label>
          <label>date of last injection (if known):</label>
        </div>

        <div className='flex align-items-center'>
          <DateWrapper name='lastInjectionDate' label='' value={lastInjectionDate} onChange={handleChange.bind(this, 'lastInjectionDate')} />
        </div>
      </div>

      <div className='flex flex-column gap-4 justify-content-center top-negative-10'>
        <label className='input-label'>Next Injection Date (+/- 7 Days)</label>
        <DateWrapper
          name='nextInjectionDate'
          label=''
          inputClassName='flex flex-fill'
          value={nextInjectionDate}
          onChange={handleChange.bind(this, 'nextInjectionDate')}
        />
      </div>
    </div>
  </div>
));

const PrescriberInformation = memo(({
  value: {
    prescriberFullName,
    prescriberNpi,
    prescriberPracticeName,
    prescriberAddress,
    prescriberState,
    prescriberZip,
    prescriberCity,
    prescriberPhone,
    prescriberFax,
    hcpSignature,
    signatureDate,
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section prescriber-information m-t-8 m-b-5 p-4'>
    <SectionLabel display='PRESCRIBER INFORMATION' />

    <div className='flex align-items-center m-b-5'>
      <Input name='prescriberFullName' label='Prescriber Last, First Name:' className='flex flex-fill m-r-4'  value={prescriberFullName} onChange={handleChange.bind(this, 'prescriberFullName')} />
      <Input name='prescriberNpi' label='NPI #:' value={prescriberNpi} onChange={handleChange.bind(this, 'prescriberNpi')} />
    </div>

    <div className='flex align-items-center m-b-5'>
      <Input name='prescriberPracticeName' label='Practice Name:' className='flex flex-fill'  value={prescriberPracticeName} onChange={handleChange.bind(this, 'prescriberPracticeName')} />
    </div>

    <div className='flex align-items-center m-b-5'>
      <Input name='prescriberAddress' label='Prescriber Address:' className='flex flex-fill m-r-4' value={prescriberAddress} />
      <Input
      name='prescriberState'
      label='State:'
      className='m-r-4'
      inputClassName='max-width-165'
      value={prescriberState}
    />
      <Input
        type='text'
        name='prescriberZip'
        label='Zip:'
        inputClassName='max-width-90'
        value={prescriberZip}
      />
    </div>

    <div className='flex align-items-center m-b-5'>
      <Input name='prescriberCity' label='City:' className='flex flex-fill m-r-4' value={prescriberCity} />
      <Input name='prescriberPhone' label='Phone:' className='m-r-4' value={prescriberPhone} />
      <Input name='prescriberFax' label='Fax:' value={prescriberFax} />
    </div>

    <div className='flex align-items-center'>
      <SignatureWrapper
        name='hcpSignature'
        label='Electronically approved by:'
        value={hcpSignature}
        onChange={handleChange.bind(this, 'hcpSignature')}
      />
      <DateWrapper name='signatureDate' label='Date:' value={signatureDate} max={MaxDate} />
    </div>
  </div>
));

const Dosage = memo(({
  value: {
    icd10Diagnosis = '',
    otherICD10Code = '',
    noOfPNRRefillsTypeA = '',
    noOfPNRRefillsTypeB = '',
    noOfAnaphylaxisRefillsTypeA = '',
    ...rest
  },
  onChange: handleChange = () => {}
}) => (
  <div className='section therapy-order m-t-8 m-b-5 p-4'>
    <SectionLabel display="APRETUDE DOSAGE & ICD-10 CODES" />

    <div className='flex flex-column gap-12'>
      <div className='flex align-items-center gap-16'>
        <label className='input-label'>THERAPY ORDER: APRETUDE (cabotegravir) 600-mg kit</label>

        <div className='flex align-items-center gap-'>
          <Checkbox
            title="checkbox-asoc-on-site"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={rest['checkbox-asoc-on-site']}
            onChange={(_, _value) => { handleChange('checkbox-asoc-on-site', { target: { value: _value } }); }}
          />
          <label className='input-label'>ASOC may administer on site</label>
        </div>

        <div className='flex align-items-center'>
          <Checkbox
            title="checkbox-pharmacist-may-administer"
            className='checkbox'
            inputProps={{ 'aria-label': 'primary checkbox' }}
            checked={rest['checkbox-pharmacist-may-administer']}
            onChange={(_, _value) => { handleChange('checkbox-pharmacist-may-administer', { target: { value: _value } }); }}
          />
          <label className='input-label'>Pharmacist may administer</label>
        </div>
      </div>

      <div className='flex flex-column justify-content-center gap-8'>
        <div className='flex align-items-center'>
          <label className={`input-label m-r-1`}>Please check all that apply:</label>
        </div>
        
        <div className='grid align-items-center col-5 auto-rows-30 font-14'>
          <div
            className={`
              flex
              align-items-center
              p-1
              bold
              mn-col
              mn-col-border
              bg-dark-primary-v2
              color-white
            `}
          >
            Prescription/Schedule
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              bold
              mn-col
              mn-col-border
              bg-dark-primary-v2
              color-white
            `}
          >
            Medication
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              bold
              mn-col
              mn-col-border
              bg-dark-primary-v2
              color-white
            `}
          >
            Quantity
          </div>

          <div
            className={`
              flex
              align-items-center
              min-width-165
              p-1
              bold
              mn-col
              mn-col-border
              bg-dark-primary-v2
              color-white
            `}
          >
            Refills
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              bold
              mn-col
              mn-col-border
              col-5
              bg-dark-primary-v2
              color-white
            `}
          >
            Directions
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              bold
              mn-col
              mn-col-border
              full-col
              col-5
              bg-light-primary
            `}
          >
            Every-2-Month Dosing
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
            `}
          >
            <Checkbox
              title="checkbox-asoc-dose-type-a"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-asoc-dose-type-a']}
              onChange={(_, _value) => { handleChange('checkbox-asoc-dose-type-a', { target: { value: _value } }); }}
            />
            <label className='font-14'>APRETUDE 600-mg kit</label>
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
            `}
          >
            600-mg single-dose vial of cabotegravir
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
            `}
          >
            1 dosing kit
          </div>
          <div
            className={`
              flex
              align-items-center
              min-width-165
              p-1
              mn-col
              mn-col-border
            `}
          >
            <Input
              name='noOfPNRRefillsTypeA'
              label=''
              className='flex flex-fill'
              inputClassName='min-width-50'
              value={noOfPNRRefillsTypeA}
              onChange={handleChange.bind(this, 'noOfPNRRefillsTypeA')}
            />
          </div>
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
              col-5
            `}
          >
            Month 1 & Month 2: 1 injection intramuscularly
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
            `}
          >
            <Checkbox
              title="checkbox-asoc-dose-type-b"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-asoc-dose-type-b']}
              onChange={(_, _value) => { handleChange('checkbox-asoc-dose-type-b', { target: { value: _value } }); }}
            />
            <label className='font-14'>APRETUDE 600-mg kit</label>
          </div>
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
            `}
          >
            600-mg single-dose vial of cabotegravir
          </div>
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
            `}
          >
            1 dosing kit
          </div>
          <div
            className={`
              flex
              flex-column
              justify-content-center
              min-width-165
              gap-2
              p-1
              mn-col
              mn-col-border
            `}
          >
            <div className='flex align-items-center'>
              <Checkbox
                title="checkbox-pnr-refills"
                className='checkbox'
                inputProps={{ 'aria-label': 'primary checkbox' }}
                checked={rest['checkbox-pnr-refills']}
                onChange={(_, _value) => { handleChange('checkbox-pnr-refills', { target: { value: _value } }); }}
              />
              <label className='font-14'>PRN refills for 1 year or # of refills</label>
            </div>

            <Input
              name='noOfPNRRefillsTypeB'
              label=''
              className='flex flex-fill'
              inputClassName='min-width-50'
              value={noOfPNRRefillsTypeB}
              onChange={handleChange.bind(this, 'noOfPNRRefillsTypeB')}
            />
          </div>
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
              col-5
              gap-1
            `}
          >
            Month 4+: 1 injection intramuscularly, every 2 months
          </div>

          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
              mn-col-border-bottom
            `}
          >
            <Checkbox
              title="checkbox-anaphylaxis-dose-type-a"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-anaphylaxis-dose-type-a']}
              onChange={(_, _value) => { handleChange('checkbox-anaphylaxis-dose-type-a', { target: { value: _value } }); }}
            />
            <label className='font-14'>Anaphylaxis Kit</label>
          </div>
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
              mn-col-border-bottom
            `}
          />
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
              mn-col-border-bottom
            `}
          >
            1 kit
          </div>
          <div
            className={`
              flex
              flex-column
              justify-content-center
              min-width-165
              gap-2
              p-1
              mn-col
              mn-col-border
              mn-col-border-bottom
            `}
          >
            <Input
              name='noOfAnaphylaxisRefillsTypeA'
              label=''
              className='flex flex-fill'
              inputClassName='min-width-50'
              value={noOfAnaphylaxisRefillsTypeA}
              onChange={handleChange.bind(this, 'noOfAnaphylaxisRefillsTypeA')}
            />
          </div>
          <div
            className={`
              flex
              align-items-center
              p-1
              mn-col
              mn-col-border
              mn-col-border-bottom
              col-5
              gap-1
            `}
          >
            Administer anaphylaxis protocol as needed
          </div>
        </div>
      </div>

      <div className='flex flex-column gap-8'>
        <div className='flex align-items-center gap-16'>
          <div className='flex col-1 align-items-center gap-4'>
            <span className='input-label'>ICD-10 Code</span>
            <span className='italic font-14'>(fill in):</span>
            <span className='required font-24'>*</span>
          </div>
          <Input name='icd10Diagnosis' label='' className='flex flex-fill' value={icd10Diagnosis} onChange={handleChange.bind(this, 'icd10Diagnosis')} />
        </div>

        <div className='flex align-items-center gap-16 d-none'>
          <div
            className={`
              flex
              align-items-center
            `}
          >
            <Checkbox
              title="checkbox-Z114"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-Z114']}
              onChange={(_, _value) => { handleChange('checkbox-Z114', { target: { value: _value } }); }}
            />
            <label className='font-14'>Z11.4 Encounter for screening for human immunodeficiency virus</label>
          </div>

          <div
            className={`
              flex
              align-items-center
            `}
          >
            <Checkbox
              title="checkbox-OTHER"
              className='checkbox'
              inputProps={{ 'aria-label': 'primary checkbox' }}
              checked={rest['checkbox-OTHER']}
              onChange={(_, _value) => { handleChange('checkbox-OTHER', { target: { value: _value } }); }}
            />
            <label className='font-14'>Other</label>
          </div>

          <Input name='otherICD10Code' label='' className='flex flex-fill' value={otherICD10Code} onChange={handleChange.bind(this, 'otherICD10Code')} />
        </div>
      </div>
    </div>
  </div>
));

const Payment = memo(({
  value: {
    ...rest
  },
  onChange: handleChange = () => {},
}) => (
  <div className='section payment-information m-t-8 m-b-5 p-4'>
    <SectionLabel display='PAYMENT INFORMATION' />

    <div className='flex flex-column gap-16'>
      <div className='flex align-items-center'>
        <Checkbox
          title="checkbox-insurance"
          className='checkbox'
          inputProps={{ 'aria-label': 'primary checkbox' }}
          checked={rest['checkbox-insurance']}
          onChange={(_, _value) => { handleChange('checkbox-insurance', { target: { value: _value } }); }}
        />
        <label className='input-label'>Insurance</label>
      </div>
      
      <ul className='flex align-items-center m-0 p-l-4'>
        <li>See attached or collect insurance information from patient for payment</li>
      </ul>
    </div>
  </div>
));

const LabOrder = memo(() => (
  <div className='section payment-information m-t-8 m-b-5 p-4'>
    <SectionLabel display='LAB ORDER' />

    <ul className='flex flex-column gap-16 m-0 p-l-4'>
      <li>HIV 1/2 Ag/Ab, Fourth Generation, W/Reflexes (CPT Code 87389)</li>
      <li>HIV-1 RNA, Qualitative Real Time PCR (CPT Code 87535)</li>
      <li><span className='bold'>Diagnosis Code:</span> Z2981-Encounter for HIV Pre-Exposure Prophylaxis with modifier 33</li>
      <li>This is a standing order to be collected once per month or as needed for Apretude Injection Schedule starting today and ending in one year</li>
    </ul>
  </div>
));

const LabOrderV2 = memo(() => (
  <div className='section payment-information m-t-8 m-b-5 p-4'>
    <SectionLabel display='LAB ORDER' />

    <ul className='flex flex-column gap-16 m-0 p-l-4'>
      <li>HIV 1/2 Ag/Ab, Fourth Generation, W/Reflexes (CPT Code 87389)</li>
      <li><span className='bold'>Diagnosis Code:</span> Z2981-Encounter for HIV Pre-Exposure Prophylaxis with modifier 33</li>
    </ul>
  </div>
));

const FaxInformation = memo(({
  value: {
    prescriberFax,
  },
}) => (
  <div className='section fax-information border-none m-b-5'>
    <div className='flex align-items-center justify-content-center gap-8'>
      <p className='font-24 m-0 p-0'>Fax results to <span className='bold'>{prescriberFax}</span> (Please provide partial results if available)</p>
    </div>
  </div>
));

const SectionA = ({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {},
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />
    <div className={`content flex flex-column ${pageElementSelector}`}>
      <PatientInformation value={value} onChange={handleChange} />
      <ClinicalInformation value={value} onChange={handleChange} />
      <PatientStatus value={value} onChange={handleChange} />
      <Dosage value={value} onChange={handleChange} />
      <PrescriberInformation value={value} onChange={handleChange} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
);

export const SectionB = memo(({
  value = {},
  consultInfo = {},
  className = '',
  heading: Heading = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => {
  const { qAndAs } = useMemo(() => {
    try {
      const qAndAs = consultInfo.ProductConsultDetails;
      return { qAndAs };
    } catch(e) {
      return { qAndAs: [] };
    }
  }, [consultInfo]);

  return (
    <QuestionAnswers
      value={value}
      qAndAs={qAndAs}
      className={className}
      heading={Heading}
      footer={Footer}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
});

export const SectionC = memo(({
  value = {},
  notes = {},
  className = '',
  heading: Heading = Fragment,
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div className={`page border-primary flex flex-column max-width-1024 ${pageWithMaxPossibleWidthSelector} ${className} d-none`}>
    <Heading value={value} onChange={handleChange} />
    <div className='content flex flex-column flex-fill m-b-5'>
      <SOAPNotes notes={notes} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
));

const SectionD = ({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {},
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />
    <div className={`content flex flex-column ${pageElementSelector}`}>
      <Date value={value} onChange={handleChange} />
      <PatientInformation value={value} onChange={handleChange} />
      <Payment value={value} onChange={handleChange} />
      <LabOrder value={value} onChange={handleChange} />
      <PrescriberInformation value={value} onChange={handleChange} />
      <FaxInformation value={value} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
);

const SectionE = ({
  value = {},
  className = '',
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {},
}) => (
  <div className={`page border-primary ${pageSelector} ${className}`}>
    <Heading value={value} onChange={handleChange} />
    <div className={`content flex flex-column ${pageElementSelector}`}>
      <Date value={value} onChange={handleChange} />
      <PatientInformation value={value} onChange={handleChange} />
      <Payment value={value} onChange={handleChange} />
      <LabOrderV2 value={value} onChange={handleChange} />
      <PrescriberInformation value={value} onChange={handleChange} />
      <FaxInformation value={value} />
    </div>
    <Footer onSubmit={handleSubmit} />
  </div>
);

export const CMNTemplateForApretude = ({
  id='',
  reference,
  className = '',
  value = {},
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  onSubmit: handleSubmit = () => {},
  onChange: handleChange = () => {}
}) => (
  <div ref={reference} id={id} className={CMNApretudeTemplateIdentifier}>
    <SectionA
      value={value}
      className={className}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
    
    {
      false && (
        <Fragment>
          <SectionD
            value={value}
            className={className}
            onSubmit={handleSubmit}
            onChange={handleChange}
          />

          <SectionE
            value={value}
            className={className}
            onSubmit={handleSubmit}
            onChange={handleChange}
          />
        </Fragment>
      )
    }

    <SectionC
      value={value}
      notes={notes}
      className={className}
      heading={Heading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />

    <SectionB
      value={value}
      consultInfo={consultInfo}
      className={className}
      heading={Heading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  </div>
);

export const TemplateDownloadManager = ({
  id = '',
  notes = {},
  productInfo = {},
  patientInfo = {},
  consultInfo = {},
  formField = { ...DEFAULT_FORMFIELD },

  getCmnPdfData = async () => {},
  setFormField = () => {}
}) => {
  const reference = useRef();

  const handleSubmit = (event) => {
    event.preventDefault();
    preparePDF(
      CMNApretudeTemplateIdentifier,
      [pageSelector, pageWithMaxPossibleWidthSelector],
      Letter,
      pageElementSelector,
      ImageQuality[100],
      generatePDF
    ).then(() => {
      // code snippet if required
    });
  };

  const handleChange = (name, event) => {
    setFormField({ ...formField, [name]: event.target.value });
  };

  const loadData = async () => {
    const response  = await getCmnPdfData({
      OrderId: productInfo.OrderId,
      PaitentId: patientInfo.UserId,
      template: Apretude,
    });

    const pdfData = (() => {
      try {
        return response.data.Data || DEFAULT_PDF_INFO();
      } catch(e) {
        return DEFAULT_PDF_INFO();
      }
    })();

    let nextFormField = {
      ...DEFAULT_FORMFIELD,
    };

    try {
      nextFormField = {
        ...nextFormField,

        patientDob: moment(pdfData.patientInfo.dob).format(DateFormat),
        patientFullName: `${pdfData.patientInfo.lastName}, ${pdfData.patientInfo.firstName}`,
        patientHeight: patientInfo.Height,
        patientWeight: patientInfo.Weight,
        patientAddress: pdfData.patientInfo.address,
        patientState: pdfData.patientInfo.state,
        patientZip: pdfData.patientInfo.zip,
        patientCity: pdfData.patientInfo.city,
        patientPhone: pdfData.patientInfo.phone,
        patientEmail: pdfData.patientInfo.email,

        prescriberPreName: pdfData.prescriberInfo.preName,
        prescriberFullName: `${pdfData.prescriberInfo.lastName}, ${pdfData.prescriberInfo.firstName}`,
        prescriberPracticeName: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberPracticeName,
        prescriberNpi: pdfData.prescriberInfo.npiNo,
        prescriberFax: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.prescriberFax,

        lastVisitDate: MaxDate,

        hcpSignature: `${pdfData.provider.signature.prifix} ${pdfData.provider.signature.firstName} ${pdfData.provider.signature.lastNmae}`,
        signatureDate: MaxDate,

        diagnosisDescription: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.diagnosisDescription,
        icd10Diagnosis: DEFAULT_FORMFIELD_WHEN_FIELD_IS_MISSING.icd10Diagnosis,
      };
    } catch (e) {

    }

    // eslint-disable-next-line no-unused-vars
    const questionAnswersMap = new Map([[0, []]]);
    const productInfoWithQuestionAnswers = consultInfo.ProductConsultDetails.find(({ ProductId }) => ProductId === productInfo.pdfInfo.productId);
    if (productInfoWithQuestionAnswers && productInfoWithQuestionAnswers.QuestionAnswers) {
      for (let i = 0; i < productInfoWithQuestionAnswers.QuestionAnswers.length; i++) {
        const qAndA = productInfoWithQuestionAnswers.QuestionAnswers[i];
        let qAndAs = [qAndA];
        if (questionAnswersMap.has(qAndA.QuestionId)) {
          const _qAndAs = questionAnswersMap.get(qAndA.QuestionId);
          qAndAs = [..._qAndAs, qAndA];
        }
        questionAnswersMap.set(qAndA.QuestionId, qAndAs);
      }
    }

    const config = (() => {
      try {
        return new Map(productInfo.StoreFrontData.StorefrontApplicationSettings) || new Map([['', {}]]);
      } catch(e) {
        return new Map([['', {}]]);
      }
    })();

    if (config.has(FAX_ProvidersAndCreatePDF)) {
      nextFormField.storeFax = config.get(FAX_ProvidersAndCreatePDF).value;
    }

    if (config.has(Email_ProvidersAndCreatePDF)) {
      nextFormField.storeEmail = config.get(Email_ProvidersAndCreatePDF).value;
    }

    if (config.has(QID_DrugAllergiesInput)) {
      const qIds = config.get(QID_DrugAllergiesInput).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField.drugAllergies = '';

          if (qAndA?.AnswerText?.length > 0) {
            nextFormField.drugAllergies = qAndA.AnswerText.trim();
            break;
          }
        }

        if (nextFormField.drugAllergies !== '') {
          break;
        }
      }
    }

    if (config.has(QID_IsPatientPregnantCheckbox)) {
      const qIds = config.get(QID_IsPatientPregnantCheckbox).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField['checkbox-pregnant'] = CheckBoxChecked.no;

          const regex = /(yes)/gmi;
          if (regex.test(qAndA.AnswerText)) {
            nextFormField['checkbox-pregnant'] = CheckBoxChecked.yes;
            break;
          }
        }

        if (nextFormField['checkbox-pregnant'] === CheckBoxChecked.yes) {
          break;
        }
      }
    }

    if (config.has(QID_CurrentlyTakingAppretudeCheckbox)) {
      const qIds = config.get(QID_CurrentlyTakingAppretudeCheckbox).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField['checkbox-ntoth'] = false;
          nextFormField['checkbox-conth'] = true;

          const regex = /(no)/gmi;
          if (regex.test(qAndA.AnswerText)) {
            nextFormField['checkbox-ntoth'] = true;
            nextFormField['checkbox-conth'] = false;
            break;
          }
        }

        if (nextFormField['checkbox-ntoth'] === true) {
          break;
        }
      }
    }

    if (config.has(PID_ShouldCabotegravir)) {
      const pIds = config.get(PID_ShouldCabotegravir).value.split(',');
      for (let i = 0; i < pIds.length; i++) {
        nextFormField['checkbox-should-cabotegravir'] = CheckBoxChecked.no;
        nextFormField['checkbox-has-cabotegravir'] = '';

        const productId = Number(productInfo?.pdfInfo?.productId || 0);
        const pIdInAppSetting = Number(pIds[i]);
        if (productId === pIdInAppSetting) {
          nextFormField['checkbox-should-cabotegravir'] = CheckBoxChecked.yes;
          nextFormField['checkbox-has-cabotegravir'] = CheckBoxChecked.no;
          break;
        }
      }
    }

    if (config.has(QID_LastInjectionDate)) {
      const qIds = config.get(QID_LastInjectionDate).value.split(',');
      for (let i = 0; i < qIds.length; i++) {
        const qId = Number(qIds[i]);
        if (!questionAnswersMap.has(qId)) continue;

        const qAndAs = questionAnswersMap.get(qId);
        for (let j = 0; j < qAndAs.length; j++) {
          const qAndA = qAndAs[j];
          nextFormField.lastInjectionDate = '';

          if (qAndA?.AnswerText?.length > 0) {
            try {
              nextFormField.lastInjectionDate = moment(qAndA.AnswerText).format(DateFormat);
            } catch (e) {

            }
            break;
          }
        }

        if (nextFormField.lastInjectionDate !== '') {
          break;
        }
      }
    }

    if (nextFormField['checkbox-ntoth'] === true) {
      nextFormField['checkbox-asoc-dose-type-a'] = true;
      nextFormField['checkbox-asoc-dose-type-b'] = true;

      nextFormField.noOfPNRRefillsTypeA = '1 refill';
      nextFormField.noOfPNRRefillsTypeB = '0 refill';
    }

    if (nextFormField['checkbox-ntoth'] === false) {
      nextFormField['checkbox-asoc-dose-type-a'] = false;
      nextFormField['checkbox-asoc-dose-type-b'] = true;

      nextFormField.noOfPNRRefillsTypeA = '';
      nextFormField.noOfPNRRefillsTypeB = '1 refill';
    }

    console.log('@logger - initializing the pdf template', {
      config,
      pdfData,
      pdfInfo: productInfo.pdfInfo,
    });

    setFormField(nextFormField);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <CMNTemplateForApretude
      id={id}
      reference={reference}
      className='m-b-4 p-4'
      value={formField}
      notes={notes}
      productInfo={productInfo}
      patientInfo={patientInfo}
      consultInfo={consultInfo}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
};

export default memo(TemplateDownloadManager);
